/**
 * The repository: find a document, read it, do what is being asked of you.
 *
 * What the reader sees is already permission-trimmed by the server — the list
 * comes back from an ACL pre-filtered query, so a document the caller may not
 * see is not "hidden here", it was never in the result. The `rights` on each
 * row drive which controls appear; the server denies regardless.
 *
 * Three things about the shape of this screen are deliberate:
 *
 * - **One "Waiting for you" list.** Acknowledgements and approvals used to be
 *   two tabs that appeared and vanished depending on whether the caller had
 *   work. A list that is sometimes not there cannot be checked as a habit, so
 *   it is always present and always says so when it is empty.
 * - **Folders are a rail, not a dropdown.** A `select` hides the shape of the
 *   library from the person trying to learn it.
 * - **Status is a sentence.** A chip alone says `approved`; the line under it
 *   says whether the reader should be doing what the document says today.
 *
 * The Ask panel shows CITATIONS as first-class content, not as a footnote: an
 * answer about a controlled document is only usable if the reader can see which
 * release it came from and open it. Each citation carries title, version and
 * effective date for exactly that reason.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AddDocumentsModal } from './AddDocumentsModal'
import { ApprovalQueue } from './ApprovalQueue'
import { ComplianceView } from './ComplianceView'
import { DocumentDetail } from './DocumentDetail'
import { Icon } from './icons'
import { ReindexConfirm } from './ReindexConfirm'
import {
  BROWSE_ORDER_LABELS, documentFacts, formatWhen, humaniseState,
  orderFolders, STATE_CHIP_TONE, STATE_LABELS, STATE_MEANING, STATE_ORDER,
  type DmsAnswer, type DmsBrowseOrder, type DmsClient, type DmsConditionGroup,
  type DmsDocType, type DmsDocumentRow, type DmsFilterSpec, type DmsFolder,
  type DmsDocumentSource,
  type DmsOutstandingAck, type DmsPendingSignature,
  type DmsReindexStatus, type DmsRights,
  type DmsSavedFilter, type DmsUser,
} from './contracts'
import { useAuthedSrc, useNearViewport } from './authedSrc'
import { personName, personNameOrYou, usePeople } from './people'
import {
  btnPrimaryStyle, btnStyle, cardStyle, errStyle, explainStyle, footerStyle,
  headerStyle, headingStyle, inputStyle, linkStyle, metaStyle,
  mutedBlockStyle, mutedStyle, RADIUS, rowStyle, scrollAreaStyle, searchStyle,
  sectionTitleStyle, T, TYPE,
} from './styles'
import { CountBadge, EmptyState, FactChip, StatusChip, TabButton } from './ui'
import {
  buildFilterFields, countConditions, FilterBuilderModal, MultiSelectPill,
  SavedFilterMenu, specToConditions,
} from './BrowseFilters'
import { MarkdownText } from '../markdown'
import { useHostNamesObject } from '../objectChrome'
import { useScrollDirectionHide } from '../autoHide'

export interface RepositoryViewProps {
  client: DmsClient
  title?: string
  /** The repository's object name. Only used to ask whether the surrounding
   *  chrome (the Run shell) already names THIS repository — when it does, the
   *  header below drops its own heading and leads with the tabs instead of
   *  printing the name twice. Omit on a surface that names nothing (the
   *  Document Designer's run tab). See ../objectChrome. */
  objName?: string
  /** Whether the Ask panel is offered at all (repository `ai.qa_enabled`). */
  qaEnabled?: boolean
  userKey?: string
  /** Deep-link straight to one document. */
  initialDocumentId?: string
  onDocumentChange?: (documentId: string | null) => void
  /**
   * Opens the authoring sub-module. Optional so a host that does not serve
   * `/authoring/*` simply never offers to write a document, and so this view's
   * consumers do not pull an editor most of them never open — see
   * `onAuthor` in the platform and runtime panes for the lazy mount.
   */
  onAuthor?: (mode:
    | { kind: 'new'; doctypeId: string; folderId?: string; title?: string }
    | { kind: 'open'; documentId: string; versionId?: string }
    | { kind: 'compare'; documentId: string; versionA: string; versionB: string }
  ) => void
  /**
   * Open the RECORD a Document Form filed a document from.
   *
   * Passed straight through to `DocumentDetail`, which decides when to offer
   * it. Optional: a host that cannot navigate to a transaction omits it.
   */
  onOpenSource?: (source: DmsDocumentSource) => void
}

type Tab = 'browse' | 'ask' | 'waiting' | 'records'

/** How the library lists things. Cards show what a document LOOKS like; the
 *  list fits more on screen. */
type ViewMode = 'cards' | 'list'

/**
 * How many documents one browse request brings back.
 *
 * The library is READ a screenful at a time, so it is fetched a screenful at
 * a time: the first rows arrive at the same speed whether the repository
 * holds forty documents or forty thousand, and the next chunk is asked for as
 * the reader approaches the end of what they already have. Nothing about what
 * is being listed moves to the client with it — the search, the pills, the
 * order and the counts are all still answered by the server (see `FilterBar`),
 * so a chunk is a chunk of the whole library rather than of whatever happened
 * to be loaded.
 *
 * Ten, so the screen is useful after one small request rather than after one
 * that happens to contain the whole library — a chunk large enough to swallow
 * an ordinary repository in a single call is the behaviour this replaced
 * wearing a smaller number. The endpoint's own ceiling is 200; `total` says
 * how much more there is, and the observer below fetches the rest as it is
 * scrolled to. One number for every repository, because one component draws
 * them all.
 *
 * A pane taller than ten cards will chain a second chunk immediately — the
 * marker is still in view, so there is more room than rows. That is the point:
 * the list fills the space it has and then waits for the reader.
 */
const PAGE_SIZE = 10

/**
 * Which of the two the reader last chose, remembered in the browser.
 *
 * Choosing a layout is a statement about how this person reads a library, not
 * about the visit — someone who switches to the list because they scan by name
 * means it for every visit after, and having to say so again on each return
 * made the toggle feel like it had not worked. Kept per reader (`userKey`) so
 * two people sharing a browser do not inherit each other's choice, and per
 * browser rather than on the server because it is a personal, view-only
 * preference — the same reasoning as the global search panel's own toggle.
 */
const VIEW_KEY = 'twasta.dms.view'

function viewStorageKey(userKey: string | undefined): string {
  return userKey ? `${VIEW_KEY}.${userKey}` : VIEW_KEY
}

function readView(key: string): ViewMode {
  if (typeof localStorage === 'undefined') return 'cards'
  try {
    return localStorage.getItem(key) === 'list' ? 'list' : 'cards'
  } catch {
    return 'cards'
  }
}

function writeView(key: string, view: ViewMode): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(key, view)
  } catch {
    // Blocked site data costs the preference and nothing else.
  }
}

/**
 * Everything the Ask panel has on screen.
 *
 * Held HERE rather than inside the panel because opening a citation swaps this
 * whole view for `DocumentDetail`, and coming back used to remount the panel
 * empty — the reader lost the answer they had just been reading and had no way
 * to get it back but to retype the question. State that survives a round trip
 * to a document has to live above the thing the round trip unmounts.
 */
interface AskState {
  question: string
  answer: DmsAnswer | null
  error: string
  rated: boolean
  /**
   * The citations whose passage is unfolded, keyed `<document_id>-<n>`.
   *
   * Every source is LISTED — which document an answer rests on is the point of
   * the section — but its quoted passage starts folded, so a reader sees the
   * documents at a glance instead of scrolling past a paragraph of each. It
   * lives HERE, not in the panel, for the reason the panel is controlled at
   * all: following a citation unmounts the panel, and coming back to find the
   * passage you had just opened folded shut again is the same lost place.
   */
  openCitations: string[]
}

const EMPTY_ASK: AskState = {
  question: '', answer: null, error: '', rated: false, openCitations: [],
}

export function RepositoryView({
  client, title, objName, qaEnabled, userKey, initialDocumentId, onDocumentChange,
  onAuthor, onOpenSource,
}: RepositoryViewProps) {
  // The Run shell's header already names this repository — printing it again
  // one row below is the same name twice. Stays true (heading drawn) wherever
  // nothing else names it: the Document Designer's run tab.
  const hostNamesThis = useHostNamesObject(objName || '', 'K')
  const [tab, setTab] = useState<Tab>('browse')
  const [rows, setRows] = useState<DmsDocumentRow[]>([])
  const [total, setTotal] = useState(0)
  // Fetching the NEXT chunk, as opposed to replacing the list. Two flags
  // rather than one because replacing the list blanks it while it loads and
  // appending to it must not — the rows the reader is looking at stay put.
  const [loadingMore, setLoadingMore] = useState(false)
  const [moreError, setMoreError] = useState('')
  // Set when a chunk comes back empty although `total` said there was more —
  // a document deleted while the reader was paging can do it. Without it the
  // observer below would ask for the same absent chunk forever.
  const [endReached, setEndReached] = useState(false)
  // Bumped whenever the list is REPLACED. Anything that must react to a new
  // list rather than to a longer one watches this instead of `rows`.
  const [listEpoch, setListEpoch] = useState(0)
  const [types, setTypes] = useState<DmsDocType[]>([])
  const [folders, setFolders] = useState<DmsFolder[]>([])
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [query, setQuery] = useState('')
  const [doctypeIds, setDoctypeIds] = useState<string[]>([])
  const [folderId, setFolderId] = useState('')
  const [stateKeys, setStateKeys] = useState<string[]>([])
  const [owners, setOwners] = useState<string[]>([])
  // The expression built in the dialog and applied without saving. Separate
  // from the saved ones so "apply without saving" is not a second-class path.
  const [adHoc, setAdHoc] = useState<DmsConditionGroup | null>(null)
  const [savedFilters, setSavedFilters] = useState<DmsSavedFilter[]>([])
  const [canShareFilters, setCanShareFilters] = useState(false)
  const [canSaveFilters, setCanSaveFilters] = useState(false)
  const [activeFilterIds, setActiveFilterIds] = useState<string[]>([])
  const [builder, setBuilder] = useState<
    { open: false } | { open: true; editing?: DmsSavedFilter }
  >({ open: false })
  const [filterBusy, setFilterBusy] = useState(false)
  const [filterError, setFilterError] = useState('')
  const [order, setOrder] = useState<DmsBrowseOrder>('recent')
  const [view, setView] = useState<ViewMode>(() => readView(viewStorageKey(userKey)))
  const [open, setOpen] = useState<string>(initialDocumentId || '')
  // Where in the opened document a citation pointed (page + passage). Set only
  // by the Ask tab; every other opener leaves it null and the viewer starts at
  // page one as it always did.
  const [openFocus, setOpenFocus] = useState<{ page?: number | null; excerpt?: string } | null>(null)
  // Browse's search + filters give way to the documents: they slide up as the
  // reader scrolls INTO the list and come back the moment they scroll back
  // towards the top. Reading is the long task here; filtering is the short one.
  // Measured from the bar itself (it wraps to two rows on a narrow pane, and
  // grows a "Clear all" when a filter is on), so the gate below is the height
  // hiding would actually give back rather than a guessed constant.
  const filterBarRef = useRef<HTMLDivElement>(null)
  const [filterBarHeight, setFilterBarHeight] = useState(0)
  // The list's own scroller and the marker sitting at the end of it. Together
  // they are how "the reader is getting to the bottom" is noticed — see the
  // observer further down.
  const listScrollRef = useRef<HTMLDivElement>(null)
  const endMarkerRef = useRef<HTMLDivElement>(null)
  const {
    hidden: filtersHidden, onScroll: onBrowseScroll, reveal: revealFilters,
  } = useScrollDirectionHide({ minOverflow: filterBarHeight + 8 })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  // What this caller may do to the LIBRARY (as opposed to to a document):
  // file something new, make a folder. Comes back with every browse. Null
  // until the first list arrives, and the controls it gates stay hidden while
  // it is — offering "Add documents" to somebody who will be refused is the
  // bug this replaced, so the unknown state errs the same way a refusal does.
  const [repoRights, setRepoRights] = useState<DmsRights | null>(null)
  const canAdd = Boolean(repoRights?.add)
  const [acks, setAcks] = useState<DmsOutstandingAck[]>([])
  const [ask, setAsk] = useState<AskState>(EMPTY_ASK)
  // Two capabilities that are neither always present nor always permitted.
  //
  // The approval count still gates the APPROVALS SECTION inside "Waiting for
  // you" — an approver with nothing waiting should not be shown a queue — but
  // it no longer gates a whole tab appearing and disappearing.
  //
  // The records view is administrator-only ON THE SERVER, and there is no
  // "am I an administrator" call to ask first — so the probe IS the question.
  const [pendingCount, setPendingCount] = useState(0)
  /**
   * Signatures waiting on this reader.
   *
   * The rows themselves, not a count, because unlike an approval a signature
   * is acted on FROM the document — the queue's job is to name the document
   * and say what signing it would mean, then open it.
   */
  const [pendingSigs, setPendingSigs] = useState<DmsPendingSignature[]>([])
  const [canAudit, setCanAudit] = useState(false)
  // Same probe pattern for re-indexing: the status call is administrator-only
  // on the server, so one refusal on mount hides the controls for readers.
  const [reindex, setReindex] = useState<DmsReindexStatus | null>(null)
  const [canReindex, setCanReindex] = useState(false)
  const [confirmingReindex, setConfirmingReindex] = useState(false)
  // Adding a document is ONE decision — which type, with what metadata, from a
  // file or from the editor — so it is one panel rather than two buttons that
  // each guessed half of it.
  const [adding, setAdding] = useState(false)

  const users = usePeople(client)
  const nameOf = useCallback((key: string) => personName(users, key), [users])

  /** Switching layout also records the choice for the reader's next visit. */
  const chooseView = useCallback((next: ViewMode) => {
    setView(next)
    writeView(viewStorageKey(userKey), next)
  }, [userKey])

  /**
   * Everything narrowing the list beyond the pills, as ONE group: the built
   * expression, plus a group per active saved filter. They AND, which is what
   * makes a saved filter combine with the pills and with each other rather
   * than replacing what is already selected.
   */
  const conditions = useMemo<DmsConditionGroup | null>(() => {
    const parts: DmsConditionGroup[] = []
    if (adHoc?.conditions?.length) parts.push(adHoc)
    for (const id of activeFilterIds) {
      const saved = savedFilters.find((f) => f.id === id)
      const group = specToConditions(saved?.filter)
      if (group) parts.push(group)
    }
    if (!parts.length) return null
    return parts.length === 1 ? parts[0] : { join: 'and', conditions: parts }
  }, [adHoc, activeFilterIds, savedFilters])

  /**
   * What is being listed, as one object.
   *
   * Every request carries it — the first chunk and each one after — so a page
   * appended to the list can never be a page of a different search than the
   * rows it is appended to.
   */
  const criteria = useMemo(() => ({
    query, folderId, order,
    doctypeIds: doctypeIds.length ? doctypeIds : undefined,
    states: stateKeys.length ? stateKeys : undefined,
    owners: owners.length ? owners : undefined,
    conditions,
  }), [query, folderId, order, doctypeIds, stateKeys, owners, conditions])

  /**
   * Which listing the rows on screen belong to.
   *
   * A chunk that arrives after the reader has changed a filter describes a
   * library they are no longer looking at, and appending it would splice two
   * result sets together. Each request remembers the sequence it was issued
   * under; a late one is dropped rather than shown.
   */
  const browseSeq = useRef(0)

  /**
   * What `loadMore` needs to know, read through a ref rather than closed over.
   *
   * It keeps the callback stable for the whole listing, which is what lets
   * the observer below be bound per listing instead of being torn down and
   * rebuilt on every appended row.
   */
  const paging = useRef({ loaded: 0, total: 0, busy: false, done: false })
  paging.current.loaded = rows.length
  paging.current.total = total
  paging.current.busy = loading || loadingMore
  paging.current.done = endReached

  const load = useCallback(async () => {
    const seq = ++browseSeq.current
    setLoading(true)
    setError('')
    setMoreError('')
    setEndReached(false)
    // A chunk still in flight belongs to the listing being replaced: its
    // result is about to be dropped on the sequence check, so the flag it
    // raised is cleared here rather than in a `finally` that will decline to
    // run. Without this, changing a filter mid-fetch left the list believing
    // it was forever loading more and it never asked again.
    setLoadingMore(false)
    try {
      const result = await client.browse({
        ...criteria,
        limit: PAGE_SIZE,
        offset: 0,
      })
      if (browseSeq.current !== seq) return
      setRows(result.rows)
      setTotal(result.total)
      setListEpoch((n) => n + 1)
      // Repository-scope rights, refreshed with the list they came with — a
      // grant changed while the screen is open takes effect on the next load
      // rather than at the next sign-in.
      setRepoRights(result.rights || null)
    } catch (browseError) {
      if (browseSeq.current !== seq) return
      setRows([])
      // Cleared with the rows it counted: a stale total over an empty list
      // reads as "showing 0 of 137", which describes nothing that happened.
      setTotal(0)
      setListEpoch((n) => n + 1)
      setRepoRights(null)
      setError(browseError instanceof Error ? browseError.message
        : 'The repository could not be listed.')
    } finally {
      if (browseSeq.current === seq) setLoading(false)
    }
  }, [client, criteria])

  /**
   * The next chunk, appended to what is already on screen.
   *
   * The offset is the number of rows held rather than a page counter, so the
   * request asks for exactly what is missing. Documents are still deduplicated
   * by id on the way in: something filed while the reader was paging shifts
   * every later row down by one, and an offset chunk can then repeat a row
   * that is already above it.
   */
  const loadMore = useCallback(async () => {
    const state = paging.current
    if (state.busy || state.done || state.loaded >= state.total) return
    const seq = browseSeq.current
    const offset = state.loaded
    setLoadingMore(true)
    setMoreError('')
    try {
      const result = await client.browse({
        ...criteria,
        limit: PAGE_SIZE,
        offset,
      })
      if (browseSeq.current !== seq) return
      if (!result.rows.length) {
        setEndReached(true)
      } else {
        setRows((prev) => {
          const seen = new Set(prev.map((row) => String(row.id)))
          return [...prev, ...result.rows.filter((row) => !seen.has(String(row.id)))]
        })
      }
      setTotal(result.total)
    } catch (moreErr) {
      if (browseSeq.current !== seq) return
      // Reported under the list rather than over it: the rows already read
      // are still good, and the only thing that failed is getting more.
      setMoreError(moreErr instanceof Error ? moreErr.message
        : 'The next documents could not be loaded.')
    } finally {
      if (browseSeq.current === seq) setLoadingMore(false)
    }
  }, [client, criteria])

  /** The saved list. Absent on a host that does not serve them, which simply
   *  means the bar offers the builder and no saved list. */
  const reloadSavedFilters = useCallback(async () => {
    if (!client.savedFilters) return
    try {
      const result = await client.savedFilters()
      setSavedFilters(result.filters || [])
      setCanShareFilters(!!result.can_share)
      setCanSaveFilters(!!result.can_save)
    } catch {
      // A repository that cannot list saved filters still browses; the bar
      // just shows none rather than an error nobody can act on.
      setSavedFilters([])
      setCanSaveFilters(false)
    }
  }, [client])

  useEffect(() => { void reloadSavedFilters() }, [reloadSavedFilters])

  useEffect(() => { void load() }, [load])

  const reloadCounts = useCallback(async () => {
    if (!client.folderCounts) return
    try {
      setCounts(await client.folderCounts())
    } catch {
      // Counts are a hint beside a folder name; the tree works without them.
    }
  }, [client])

  const reloadFolders = useCallback(async () => {
    try {
      setFolders(await client.folders())
    } catch {
      // The tree is navigation; the list works without it.
    }
    void reloadCounts()
  }, [client, reloadCounts])

  const reloadPending = useCallback(async () => {
    if (!client.pendingApprovals) return 0
    try {
      const waiting = await client.pendingApprovals()
      setPendingCount(waiting.length)
      return waiting.length
    } catch {
      // Not an approver, or the approval store is unreachable. Either way the
      // section stays hidden; it is not this view's error to report.
      return 0
    }
  }, [client])

  const reloadSignatures = useCallback(async () => {
    if (!client.pendingSignatures) return
    try {
      setPendingSigs(await client.pendingSignatures())
    } catch {
      // Not a signer, or this build has no signature tables. The section stays
      // hidden; it is not this view's error to report.
      setPendingSigs([])
    }
  }, [client])

  useEffect(() => {
    let live = true
    void (async () => {
      try {
        const [t, f] = await Promise.all([client.types(), client.folders()])
        if (!live) return
        setTypes(t)
        setFolders(f)
      } catch {
        // Types and folders are navigation aids; the list works without them.
      }
      if (client.folderCounts) {
        try {
          const c = await client.folderCounts()
          if (live) setCounts(c)
        } catch {
          // See reloadCounts.
        }
      }
      try {
        const outstanding = await client.outstandingAcks()
        if (live) setAcks(outstanding)
      } catch {
        // Acknowledgement may not be configured. Not an error to show.
      }
      if (client.pendingApprovals) {
        try {
          const waiting = await client.pendingApprovals()
          if (live) setPendingCount(waiting.length)
        } catch {
          // See reloadPending.
        }
      }
      if (client.pendingSignatures) {
        try {
          const waiting = await client.pendingSignatures()
          if (live) setPendingSigs(waiting)
        } catch {
          // See reloadSignatures.
        }
      }
      if (client.ackReport) {
        try {
          await client.ackReport()
          if (live) setCanAudit(true)
        } catch {
          // Administrator-only. A refusal here is the expected answer for
          // most callers, not a failure.
        }
      }
      if (client.reindexStatus) {
        try {
          const progress = await client.reindexStatus()
          if (live) { setReindex(progress); setCanReindex(true) }
        } catch {
          // Administrator-only; the controls stay hidden for readers.
        }
      }
    })()
    return () => { live = false }
  }, [client])

  // While a re-index runs (started here or by anyone else), poll until the
  // queue empties, then reload the list — freshly indexed content changes
  // what a search finds.
  useEffect(() => {
    if (!canReindex || !reindex?.active || !client.reindexStatus) return
    const timer = window.setInterval(async () => {
      try {
        const next = await client.reindexStatus!()
        setReindex(next)
        if (!next.active) void load()
      } catch {
        // One missed poll is not a reason to drop the progress display.
      }
    }, 2500)
    return () => window.clearInterval(timer)
  }, [canReindex, reindex?.active, client, load])

  /**
   * Report the open document to the host, on CHANGE only.
   *
   * Both guards are load-bearing when the host turns this into a URL (the Run
   * view writes `?doc=<id>`):
   *
   * - the callback is read through a ref, because a host that passes an inline
   *   arrow (every one of them does) hands us a new function identity on every
   *   render; with the callback in the dependency list, the effect re-fires,
   *   the host navigates, the navigation re-renders us, and that is an
   *   infinite loop rather than a wasted call;
   * - the last reported value is seeded from `initialDocumentId`, so mounting
   *   AT a document does not immediately re-announce it, and mounting at the
   *   list does not announce a `null` that would strip the query the host just
   *   put there.
   */
  const reportRef = useRef(onDocumentChange)
  reportRef.current = onDocumentChange
  const lastReported = useRef<string | null>(initialDocumentId || null)
  useEffect(() => {
    const value = open || null
    if (lastReported.current === value) return
    lastReported.current = value
    reportRef.current?.(value)
  }, [open])

  // Follow the deep link when it CHANGES, not only on mount. A search result
  // opened while this repository is already on screen (the host keeps the pane
  // mounted per object, not per document) would otherwise land on the list the
  // reader was already looking at. An empty prop is ignored rather than
  // treated as "close": that is the state a host with no deep-linking passes.
  useEffect(() => {
    setOpen((current) => (initialDocumentId && initialDocumentId !== current
      ? initialDocumentId
      : current))
  }, [initialDocumentId])

  const typeName = useCallback((id: string | null) => (
    types.find((t) => String(t.id) === String(id || ''))?.title || ''
  ), [types])

  const folderName = useCallback((id: string | null) => (
    folders.find((f) => String(f.id) === String(id || ''))?.name || ''
  ), [folders])

  /** Put a document in a different folder, then refresh what that changed. */
  const moveTo = useCallback(async (documentId: string, targetFolder: string) => {
    if (!client.move) return
    try {
      await client.move(documentId, targetFolder)
      await load()
      void reloadCounts()
    } catch (moveError) {
      // A folder that refuses this kind of document says so in a full
      // sentence; losing it to a silent no-op is how a cabinet's rules become
      // folklore.
      setError(moveError instanceof Error ? moveError.message
        : 'That document could not be filed there.')
    }
  }, [client, load, reloadCounts])

  /** Rearrange the folder tree, then refresh what that changed. */
  const moveFolderTo = useCallback(async (
    folderId_: string, parentId: string, position?: number,
  ) => {
    if (!client.moveFolder) return
    try {
      await client.moveFolder({ folderId: folderId_, parentId, position })
      await reloadFolders()
      void reloadCounts()
    } catch (folderError) {
      // A refused rearrangement — a name already taken there, a folder someone
      // may not reorganise — says why in a sentence, exactly as a refused
      // document move does.
      setError(folderError instanceof Error ? folderError.message
        : 'That folder could not be moved there.')
    }
  }, [client, reloadFolders, reloadCounts])

  useEffect(() => {
    const el = filterBarRef.current
    if (!el) return
    const measure = () => setFilterBarHeight(el.offsetHeight)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
    // Re-binds when Browse mounts — the element does not exist on the other tabs.
  }, [tab])

  // A list too short to scroll offers no way to scroll back UP, so anything
  // that replaces it — a filter, a folder, a different tab — brings the bar
  // back rather than leaving it collapsed with no gesture to recover it.
  // Keyed on the listing rather than on the rows: a chunk appended as the
  // reader scrolls DOWN is the one case where the bar must stay out of the
  // way, and watching `rows` would have popped it back on every chunk.
  useEffect(() => { revealFilters() }, [revealFilters, listEpoch, tab])

  /**
   * Ask for the next chunk as the end of the list comes into view.
   *
   * Observed against the list's OWN scroller, not the window: this pane
   * scrolls internally, and a window-rooted observer would count the marker
   * as visible the moment the pane was on screen. The margin asks about a
   * screenful early, so the rows are usually already there by the time the
   * reader arrives at them and the scroll never actually stops.
   *
   * Re-bound as the list grows, because re-observing re-evaluates the
   * intersection: that is what keeps a pane too tall for one chunk filling
   * itself instead of ending in a marker that is visible and silent.
   * `loadMore` guards the rest — nothing in flight, something left to fetch.
   */
  useEffect(() => {
    if (tab !== 'browse') return
    const root = listScrollRef.current
    const marker = endMarkerRef.current
    if (!root || !marker || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) void loadMore()
    }, { root, rootMargin: '400px 0px' })
    observer.observe(marker)
    return () => observer.disconnect()
  }, [loadMore, tab, view, loading, rows.length, total, endReached])

  /** Every field a condition may name — the document's own properties plus
   *  the metadata fields of every type in this repository. */
  const filterFields = useMemo(
    () => buildFilterFields(types, folders, users),
    [types, folders, users],
  )

  const applyBuilt = useCallback((group: DmsConditionGroup | null) => {
    setAdHoc(group)
    setBuilder({ open: false })
    setFilterError('')
  }, [])

  const saveBuilt = useCallback(async (
    input: { name: string; shared: boolean; group: DmsConditionGroup | null },
    editing?: DmsSavedFilter,
  ) => {
    if (!client.saveFilter) return
    setFilterBusy(true)
    setFilterError('')
    try {
      // Saved WITH the pills, so applying it later restores the whole question
      // rather than half of it — the builder's expression alone would drop the
      // kind and status the person had selected while building it.
      const spec: DmsFilterSpec = {
        query: query.trim() || undefined,
        doctypeIds: doctypeIds.length ? doctypeIds : undefined,
        states: stateKeys.length ? stateKeys : undefined,
        owners: owners.length ? owners : undefined,
        conditions: input.group,
      }
      const result = await client.saveFilter({
        name: input.name, filter: spec, shared: input.shared,
        filterId: editing?.id,
      })
      await reloadSavedFilters()
      setBuilder({ open: false })
      // The pills that went INTO the filter are cleared as it is turned on, or
      // the same narrowing would be applied twice — once live, once saved —
      // and clearing a pill afterwards would look like it did nothing.
      setDoctypeIds([]); setStateKeys([]); setOwners([]); setQuery('')
      setAdHoc(null)
      setActiveFilterIds((prev) => {
        const id = result?.filter_id || editing?.id || ''
        return !id || prev.includes(id) ? prev : [...prev, id]
      })
    } catch (saveError) {
      setFilterError(saveError instanceof Error ? saveError.message
        : 'That filter could not be saved.')
    } finally {
      setFilterBusy(false)
    }
  }, [client, query, doctypeIds, stateKeys, owners, reloadSavedFilters])

  const removeSavedFilter = useCallback(async (filter: DmsSavedFilter) => {
    if (!client.deleteFilter) return
    setActiveFilterIds((prev) => prev.filter((id) => id !== filter.id))
    try {
      await client.deleteFilter(filter.id)
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message
        : 'That filter could not be deleted.')
    }
    await reloadSavedFilters()
  }, [client, reloadSavedFilters])

  const clearAllFilters = useCallback(() => {
    setQuery(''); setDoctypeIds([]); setFolderId('')
    setStateKeys([]); setOwners([])
    setAdHoc(null); setActiveFilterIds([])
  }, [])

  const waitingCount = acks.length + pendingCount + pendingSigs.length
  const filtered = Boolean(
    query || folderId || doctypeIds.length || stateKeys.length || owners.length
    || conditions,
  )
  const canPreview = Boolean(client.thumbnailUrl)
  // What the server says is still unread, minus the case where it said so and
  // then had nothing to give.
  const hasMore = rows.length < total && !endReached

  if (open) {
    return (
      <DocumentDetail
        client={client}
        documentId={open}
        userKey={userKey}
        users={users}
        canReindex={canReindex}
        focus={openFocus}
        folders={folders}
        onClose={() => { setOpen(''); setOpenFocus(null); void load() }}
        onChanged={() => { void load(); void reloadCounts() }}
        onAuthor={onAuthor}
        onOpenSource={onOpenSource}
        onDecided={async () => { void load(); await reloadPending() }}
      />
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, background: T.bg }}>
      <header style={headerStyle}>
        {!hostNamesThis && (
          <strong style={{ fontSize: TYPE.heading, fontWeight: 600 }}>{title || 'Documents'}</strong>
        )}
        <nav style={{ display: 'flex', gap: 4 }}>
          <TabButton active={tab === 'browse'} onClick={() => setTab('browse')}>
            Browse
          </TabButton>
          {qaEnabled ? (
            <TabButton active={tab === 'ask'} onClick={() => setTab('ask')}>
              Ask a question
            </TabButton>
          ) : null}
          {/* Always present, empty or not — see the file header. */}
          <TabButton active={tab === 'waiting'} onClick={() => setTab('waiting')}>
            Waiting for you
            <CountBadge value={waitingCount}
                        tone={acks.some((a) => a.overdue) ? 'bad' : 'accent'} />
          </TabButton>
          {canAudit ? (
            <TabButton active={tab === 'records'} onClick={() => setTab('records')}>
              Records and reports
            </TabButton>
          ) : null}
        </nav>
        <div style={{ flex: 1 }} />
        {/* Progress stays visible wherever the administrator is, because a run
            they started elsewhere is still theirs to watch. The button that
            STARTS one lives on Records and reports — it is not a reader's
            control and does not belong in a reader's toolbar. */}
        {canReindex && reindex?.active ? (
          <span style={{ ...mutedStyle, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <progress value={reindex.done + reindex.parked}
                      max={reindex.total || 1} style={{ width: 110 }} />
            Rebuilding the search: {reindex.done + reindex.parked} of {reindex.total}
          </span>
        ) : null}
        {/* Only for somebody who may actually file one. `add` is a right of
            its own — a reviewer who may write new VERSIONS of a procedure is
            routinely not somebody who may introduce one — and the server
            refuses without it, so the button would be a promise it breaks.
            Still open when there are no types, PROVIDED one can be defined in
            there — otherwise the panel that creates a type is behind a button
            disabled for want of a type. */}
        {canAdd ? (
          <button type="button" style={btnPrimaryStyle} onClick={() => setAdding(true)}
                  disabled={!types.length && !client.createType}
                  title={types.length || client.createType ? undefined
                    : 'This repository has no kinds of document yet'}>
            Add documents
          </button>
        ) : null}
      </header>

      {builder.open ? (
        <FilterBuilderModal
          fields={filterFields}
          // Editing a saved one starts from ITS expression; a new one starts
          // from whatever is already applied, so "refine this and keep it" is
          // one step rather than a retype.
          initial={builder.editing ? builder.editing.filter?.conditions : adHoc}
          initialName={builder.editing?.name}
          initialShared={builder.editing?.shared}
          editingId={builder.editing?.id}
          canShare={canShareFilters}
          canSave={canSaveFilters && !!client.saveFilter}
          busy={filterBusy}
          error={filterError}
          onApply={applyBuilt}
          onSave={(input) => { void saveBuilt(input, builder.editing) }}
          onClose={() => { setBuilder({ open: false }); setFilterError('') }}
        />
      ) : null}

      {tab === 'browse' && waitingCount ? (
        <WaitingBanner
          acks={acks}
          pendingCount={pendingCount}
          signatureCount={pendingSigs.length}
          onShow={() => setTab('waiting')}
        />
      ) : null}

      {tab === 'browse' ? (
        <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
          <FolderRail
            folders={folders}
            value={folderId}
            counts={counts}
            total={total}
            filtered={filtered}
            onChange={setFolderId}
            client={client}
            canCreate={canAdd}
            onFolderCreated={reloadFolders}
            onDropDocument={client.move ? moveTo : undefined}
            onMoveFolder={client.moveFolder ? moveFolderTo : undefined}
          />

          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            {/* Collapsed to nothing rather than slid behind the header: the
                row of documents rises into the space instead of scrolling
                under a bar that is still there. `onFocusCapture` covers the
                keyboard path — "/" focuses the search box, which may be the
                thing that is currently collapsed. */}
            <div
              onFocusCapture={revealFilters}
              style={{
                display: 'grid',
                gridTemplateRows: filtersHidden ? '0fr' : '1fr',
                opacity: filtersHidden ? 0 : 1,
                transition: 'grid-template-rows 220ms ease, opacity 160ms ease',
              }}
            >
              <div style={{ minHeight: 0, overflow: 'hidden' }}>
                {/* Inside the clipping box, so it keeps its NATURAL height
                    while the row above is collapsed — that is the height the
                    list gets back, and what the hide gate is measured against. */}
                <div ref={filterBarRef}>
                <FilterBar
                  query={query} onQuery={setQuery}
                  types={types} doctypeIds={doctypeIds} onDoctypes={setDoctypeIds}
                  stateKeys={stateKeys} onStates={setStateKeys}
                  users={users} owners={owners} onOwners={setOwners}
                  userKey={userKey}
                  order={order} onOrder={setOrder}
                  view={view} onView={chooseView} canPreview={canPreview}
                  filtered={filtered}
                  onClear={clearAllFilters}
                  savedFilters={savedFilters}
                  activeFilterIds={activeFilterIds}
                  canSaveFilters={canSaveFilters}
                  offerFilters={!!client.savedFilters}
                  onToggleFilter={(id) => setActiveFilterIds((prev) => (
                    prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]
                  ))}
                  onNewFilter={() => { setFilterError(''); setBuilder({ open: true }) }}
                  onEditFilter={(filter) => {
                    setFilterError('')
                    setBuilder({ open: true, editing: filter })
                  }}
                  onDeleteFilter={removeSavedFilter}
                  builtCount={countConditions(adHoc)}
                  onEditBuilt={() => { setFilterError(''); setBuilder({ open: true }) }}
                  onClearBuilt={() => setAdHoc(null)}
                />
                </div>
              </div>
            </div>

            {error ? <p style={{ ...errStyle, margin: '0 16px 8px' }}>{error}</p> : null}

            <div ref={listScrollRef} style={{ ...scrollAreaStyle, padding: '0 16px 16px' }}
                 onScroll={onBrowseScroll}>
              {loading ? (
                <p style={mutedBlockStyle}>Loading…</p>
              ) : rows.length === 0 ? (
                <EmptyState
                  headline={filtered ? 'Nothing matches that' : 'No documents yet'}
                  hint={filtered
                    ? 'Names are searched, not the words inside the documents.'
                      + (qaEnabled ? ' To search what they say, ask a question instead.' : '')
                    : 'Anything added here is a draft until it has been approved.'}
                  action={filtered && qaEnabled ? (
                    <button type="button" style={btnStyle} onClick={() => setTab('ask')}>
                      Ask a question instead
                    </button>
                  ) : null}
                />
              ) : (
                <>
                  {view === 'cards' && canPreview ? (
                    <div style={{
                      display: 'grid', gap: 12,
                      gridTemplateColumns: 'repeat(auto-fill, minmax(232px, 1fr))',
                    }}>
                      {rows.map((row) => (
                        <DocumentCard
                          key={row.id}
                          row={row}
                          client={client}
                          userKey={userKey}
                          nameOf={nameOf}
                          typeName={typeName(row.doctype_id)}
                          folderName={folderName(row.folder_id)}
                          draggable={Boolean(client.move) && row.rights.edit}
                          onOpen={() => setOpen(row.id)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {rows.map((row) => (
                        <DocumentRow
                          key={row.id}
                          row={row}
                          userKey={userKey}
                          nameOf={nameOf}
                          typeName={typeName(row.doctype_id)}
                          folderName={folderName(row.folder_id)}
                          draggable={Boolean(client.move) && row.rights.edit}
                          onOpen={() => setOpen(row.id)}
                        />
                      ))}
                    </div>
                  )}
                  <ListEnd
                    markerRef={endMarkerRef}
                    hasMore={hasMore}
                    busy={loadingMore}
                    error={moreError}
                    remaining={Math.max(0, total - rows.length)}
                    onMore={() => { void loadMore() }}
                  />
                </>
              )}
            </div>

            <footer style={footerStyle}>
              {rows.length === total
                ? `${total} document${total === 1 ? '' : 's'} you are allowed to see`
                : `Showing ${rows.length} of ${total} documents you are allowed to see`}
              {client.move ? (
                <span style={{ marginLeft: 'auto', color: T.textFaint }}>
                  Drag a document onto a folder to file it there.
                </span>
              ) : null}
            </footer>
          </div>
        </div>
      ) : null}

      {tab === 'ask' ? (
        <AskPanel
          client={client}
          state={ask}
          onState={setAsk}
          onOpen={(id, focus) => { setOpenFocus(focus || null); setOpen(id) }}
        />
      ) : null}

      {tab === 'waiting' ? (
        <WaitingForYou
          client={client}
          acks={acks}
          pendingCount={pendingCount}
          signatures={pendingSigs}
          onOpen={setOpen}
          onAcknowledged={async () => {
            try {
              setAcks(await client.outstandingAcks())
            } catch {
              // The acknowledgement itself succeeded; a failed re-read is not
              // something to report over it.
            }
          }}
          onDecided={async () => {
            void load()
            await reloadPending()
            await reloadSignatures()
          }}
        />
      ) : null}

      {tab === 'records' ? (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {canReindex && client.reindex ? (
            <SearchIndexStrip
              reindex={reindex}
              onStart={() => setConfirmingReindex(true)}
            />
          ) : null}
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <ComplianceView client={client} onOpen={setOpen} users={users} />
          </div>
        </div>
      ) : null}

      {confirmingReindex ? (
        <ReindexConfirm
          loadPending={client.reindexPending
            ? () => client.reindexPending!() : undefined}
          onClose={() => setConfirmingReindex(false)}
          onRun={async (mode) => {
            setConfirmingReindex(false)
            try {
              const res = await client.reindex!(undefined, mode)
              setReindex(res)
            } catch (reindexError) {
              setError(reindexError instanceof Error
                ? reindexError.message
                : 'The search could not be rebuilt.')
            }
          }}
        />
      ) : null}

      {adding ? (
        <AddDocumentsModal
          client={client}
          types={types}
          folders={folders}
          folderId={folderId}
          userKey={userKey}
          onClose={() => setAdding(false)}
          onDone={() => { void load(); void reloadCounts() }}
          onAuthor={onAuthor}
          onTypesChanged={async () => {
            try {
              setTypes(await client.types())
            } catch {
              // The type was created; failing to re-read the list is not a
              // reason to tell anyone the creation failed.
            }
          }}
        />
      ) : null}
    </div>
  )
}

/**
 * What is waiting for this person, on the screen they land on.
 *
 * The "Waiting for you" tab is only useful to somebody who thinks to look at
 * it. An obligation with a due date has to come and find the reader, so it
 * says so here — once, in a line, with a way through to the list.
 */
function WaitingBanner({ acks, pendingCount, signatureCount, onShow }: {
  acks: DmsOutstandingAck[]
  pendingCount: number
  signatureCount: number
  onShow: () => void
}) {
  const overdue = acks.filter((ack) => ack.overdue).length
  const bits: string[] = []
  if (acks.length) {
    bits.push(`${acks.length} document${acks.length === 1 ? '' : 's'} to confirm you have read`)
  }
  if (pendingCount) {
    bits.push(`${pendingCount} waiting for your approval`)
  }
  if (signatureCount) {
    bits.push(`${signatureCount} waiting for your signature`)
  }
  if (overdue) {
    bits.push(`${overdue} of these ${overdue === 1 ? 'is' : 'are'} overdue`)
  }
  const total = acks.length + pendingCount + signatureCount

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      margin: '14px 16px 0', padding: '13px 16px',
      border: `1px solid ${T.warning}`, borderRadius: RADIUS.lg,
      background: T.warningWeak,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: TYPE.body, fontWeight: 600, color: T.warning }}>
          {total} thing{total === 1 ? '' : 's'} {total === 1 ? 'is' : 'are'} waiting for you
        </div>
        <div style={{ fontSize: TYPE.meta, color: T.warning, marginTop: 2 }}>
          {bits.join(' · ')}
        </div>
      </div>
      <button type="button" onClick={onShow}
              style={{ ...btnStyle, borderColor: T.warning, color: T.warning, flexShrink: 0 }}>
        Show me
      </button>
    </div>
  )
}

/**
 * The end of the list: the marker the observer watches, and the way to ask
 * for more by hand.
 *
 * The marker is what makes the scroll continuous — it sits a screenful below
 * the last row the reader can see, so the next chunk is normally already on
 * its way before they reach the bottom. The button under it is not a fallback
 * nobody meets: a reader keying down the list, a browser without an observer
 * and a chunk that FAILED all need a way to say "more" that is not a gesture.
 *
 * Nothing is drawn once the whole library is loaded — the footer already says
 * how many documents that was.
 */
function ListEnd({
  markerRef, hasMore, busy, error, remaining, onMore,
}: {
  markerRef: React.RefObject<HTMLDivElement | null>
  hasMore: boolean
  busy: boolean
  error: string
  remaining: number
  onMore: () => void
}) {
  if (!hasMore && !busy && !error) return null
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: 8, padding: '16px 0 4px',
    }}>
      <div ref={markerRef} aria-hidden style={{ width: '100%', height: 1 }} />
      {error ? <p style={{ ...errStyle, margin: 0 }}>{error}</p> : null}
      {busy ? (
        <span style={mutedStyle} role="status">Loading more documents…</span>
      ) : hasMore ? (
        <button type="button" style={btnStyle} onClick={onMore}>
          {error ? 'Try again' : `Load ${Math.min(remaining, PAGE_SIZE)} more`}
        </button>
      ) : null}
    </div>
  )
}

/**
 * Everything that narrows the list, in one row.
 *
 * All four narrowing controls are SERVER-side (`browse` takes each one), and
 * so is the order. That matters more than it looks: the list holds the chunks
 * fetched so far out of however many the reader can see, so a control that
 * reordered or filtered what happened to be loaded would be right about what
 * is on screen and wrong about the library. Changing any of them starts the
 * listing again from its first chunk, which is the only honest answer.
 */
function FilterBar({
  query, onQuery, types, doctypeIds, onDoctypes, stateKeys, onStates,
  users, owners, onOwners, userKey, order, onOrder, view, onView, canPreview,
  filtered, onClear,
  savedFilters, activeFilterIds, canSaveFilters, offerFilters,
  onToggleFilter, onNewFilter, onEditFilter, onDeleteFilter,
  builtCount, onEditBuilt, onClearBuilt,
}: {
  query: string
  onQuery: (value: string) => void
  types: DmsDocType[]
  doctypeIds: string[]
  onDoctypes: (value: string[]) => void
  stateKeys: string[]
  onStates: (value: string[]) => void
  users: DmsUser[] | undefined
  owners: string[]
  onOwners: (value: string[]) => void
  userKey?: string
  order: DmsBrowseOrder
  onOrder: (value: DmsBrowseOrder) => void
  view: ViewMode
  onView: (value: ViewMode) => void
  canPreview: boolean
  filtered: boolean
  onClear: () => void
  savedFilters: DmsSavedFilter[]
  activeFilterIds: string[]
  canSaveFilters: boolean
  /** False on a host that serves no saved filters — the controls that would
   *  do nothing are then not drawn at all. */
  offerFilters: boolean
  onToggleFilter: (id: string) => void
  onNewFilter: () => void
  onEditFilter: (filter: DmsSavedFilter) => void
  onDeleteFilter: (filter: DmsSavedFilter) => void
  /** Conditions in the unsaved, built filter — 0 when there is none. */
  builtCount: number
  onEditBuilt: () => void
  onClearBuilt: () => void
}) {
  const me = users?.find((user) => user.user_key === userKey)
  const others = (users || []).filter((user) => user.user_key !== userKey)
  const searchRef = useRef<HTMLInputElement>(null)

  const kindOptions = useMemo(
    () => types.map((type) => ({ value: type.id, label: type.title || type.type_key })),
    [types],
  )
  const stateOptions = useMemo(
    () => STATE_ORDER.map((key) => ({ value: key, label: humaniseState(key) })),
    [],
  )
  const ownerOptions = useMemo(() => {
    const list = me
      ? [{ value: me.user_key, label: 'I look after it' }]
      : []
    return [...list, ...others.map((user) => ({
      value: user.user_key, label: user.name || user.user_key,
    }))]
  }, [me, others])

  /**
   * `/` jumps to the search box, the way it does on every site that has one.
   *
   * Ignored while the caller is already typing somewhere — in a field, or in
   * anything contenteditable — because stealing focus mid-sentence to put a
   * slash in a different box is the bug this shortcut usually ships with.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return
      const target = event.target as HTMLElement | null
      const tag = target?.tagName?.toLowerCase()
      if (tag === 'input' || tag === 'textarea' || tag === 'select'
          || target?.isContentEditable) return
      event.preventDefault()
      searchRef.current?.focus()
      searchRef.current?.select()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 16px 10px' }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{
          flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', gap: 10,
          ...searchStyle, padding: '0 8px 0 14px',
        }}>
          <SearchGlyph />
          <input
            ref={searchRef}
            value={query}
            /* Titles only — `search_documents` matches `d.title`. Saying "and
               inside the documents" here would be a promise the query does not
               keep; the empty state points at Ask instead, which IS the search
               that reads the text. */
            placeholder="Search documents by name…"
            aria-label="Search documents by name"
            onChange={(event) => onQuery(event.target.value)}
            style={{
              flex: 1, minWidth: 0, border: 'none', outline: 'none',
              background: 'transparent', color: T.text,
              fontSize: TYPE.body, fontFamily: 'inherit', padding: '10px 0',
            }}
          />
          {/* Shown because it WORKS — see the effect below. A keyboard hint
              nobody wired is worse than none. */}
          <kbd style={{
            flexShrink: 0, fontSize: TYPE.small, fontFamily: T.mono,
            color: T.textFaint, border: `1px solid ${T.border}`,
            borderRadius: RADIUS.sm, padding: '2px 7px', lineHeight: 1.3,
          }}>
            /
          </kbd>
        </div>
        {canPreview ? (
          <div style={{
            display: 'flex', border: `1px solid ${T.border}`,
            borderRadius: RADIUS.md, overflow: 'hidden', flexShrink: 0,
          }}>
            <ViewToggle active={view === 'cards'} onClick={() => onView('cards')}>
              Cards
            </ViewToggle>
            <ViewToggle active={view === 'list'} onClick={() => onView('list')}>
              List
            </ViewToggle>
          </div>
        ) : null}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Each pill takes SEVERAL values — "policies or work instructions" is
            one question, and asking it used to mean two visits. */}
        <MultiSelectPill label="Any kind" options={kindOptions}
                         values={doctypeIds} onChange={onDoctypes}
                         emptyHint="This repository has no kinds of document yet." />

        <MultiSelectPill label="Any status" options={stateOptions}
                         values={stateKeys} onChange={onStates} />

        {users ? (
          <MultiSelectPill label="Anyone looks after it" options={ownerOptions}
                           values={owners} onChange={onOwners} />
        ) : null}

        {offerFilters ? (
          <SavedFilterMenu
            filters={savedFilters}
            activeIds={activeFilterIds}
            canSave={canSaveFilters}
            onToggle={onToggleFilter}
            onEdit={onEditFilter}
            onDelete={onDeleteFilter}
            onNew={onNewFilter}
          />
        ) : null}

        {/* The unsaved expression, as a pill of its own so it is as visible —
            and as removable — as the ones above it. */}
        {builtCount ? (
          <span style={{
            display: 'inline-flex', alignItems: 'center',
            borderRadius: RADIUS.full, border: `1px solid ${T.accent}`,
            background: T.accentWeak, color: T.accent,
          }}>
            <button type="button" onClick={onEditBuilt}
                    title="Change this filter"
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'inherit', font: 'inherit', fontSize: TYPE.meta,
                      padding: '6px 10px 6px 12px',
                    }}>
              {`Filter · ${builtCount} condition${builtCount === 1 ? '' : 's'}`}
            </button>
            <button type="button" onClick={onClearBuilt}
                    aria-label="Remove this filter" title="Remove this filter"
                    style={{
                      display: 'inline-flex', alignItems: 'center',
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'inherit', padding: '0 8px 0 0',
                    }}>
              <Icon name="close" size={12} />
            </button>
          </span>
        ) : (
          /* Always offered, even where the saved menu also carries it: a
             control people have to open a menu to discover is a control most
             of them never find. */
          <button type="button"
                  style={{ ...btnStyle, borderRadius: RADIUS.full,
                           color: T.textSecondary }}
                  onClick={onEditBuilt}>
            <Icon name="plus" size={12} /> Filter
          </button>
        )}

        {filtered ? (
          <button type="button" style={{ ...btnStyle, border: 'none', color: T.textSecondary }}
                  onClick={onClear}>
            Clear all
          </button>
        ) : null}

        {/* Order is not a filter — it goes to the far end, as in the design. */}
        <div style={{ flex: 1 }} />
        <select value={order} onChange={(e) => onOrder(e.target.value as DmsBrowseOrder)}
                aria-label="Order"
                style={{ ...filterSelectStyle, border: 'none', background: 'transparent',
                         color: T.textSecondary, maxWidth: 240 }}>
          {(Object.keys(BROWSE_ORDER_LABELS) as DmsBrowseOrder[]).map((key) => (
            <option key={key} value={key}>{BROWSE_ORDER_LABELS[key]}</option>
          ))}
        </select>
      </div>
    </div>
  )
}

function SearchGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden
         stroke={T.textSecondary} strokeWidth="2" strokeLinecap="round"
         style={{ flexShrink: 0 }}>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </svg>
  )
}

function ViewToggle({ active, onClick, children }: {
  active: boolean; onClick: () => void; children: React.ReactNode
}) {
  return (
    <button type="button" onClick={onClick} style={{
      padding: '7px 12px', border: 'none', cursor: 'pointer',
      fontSize: TYPE.small, fontWeight: active ? 600 : 500, fontFamily: 'inherit',
      background: active ? T.accentWeak : T.surface,
      color: active ? T.accent : T.textSecondary,
    }}>
      {children}
    </button>
  )
}

/**
 * A filter pill.
 *
 * `width: 'auto'` is load-bearing. Both stylesheets carry a global
 * `input, textarea, select { width: 100% }` — the right default for a form,
 * and fatal here: each select took the full row and the filters stacked into
 * four full-width bars instead of sitting inline as pills. An inline
 * `minWidth` does not override a global `width`.
 */
const filterSelectStyle: React.CSSProperties = {
  ...inputStyle,
  width: 'auto',
  minWidth: 0,
  maxWidth: 220,
  borderRadius: RADIUS.full,
  padding: '6px 12px',
  fontSize: TYPE.small,
}

/** What every listing says about a document, however it is laid out. */
function useDocumentFacts(row: DmsDocumentRow, userKey: string | undefined,
                          nameOf: (key: string) => string) {
  return {
    label: humaniseState(row.state),
    tone: STATE_CHIP_TONE[row.state] || 'neutral',
    meaning: STATE_MEANING[row.state],
    facts: documentFacts(row, userKey, nameOf),
  }
}

/** Start a drag that a folder can accept. */
function dragProps(row: DmsDocumentRow, draggable: boolean) {
  if (!draggable) return {}
  return {
    draggable: true,
    onDragStart: (event: React.DragEvent) => {
      event.dataTransfer.setData(DRAG_TYPE, row.id)
      event.dataTransfer.setData('text/plain', row.title || 'Untitled')
      event.dataTransfer.effectAllowed = 'move'
    },
  }
}

/** The MIME-ish key a folder looks for, so an unrelated drag (a file from the
 *  desktop, text from another app) is not mistaken for a document. */
const DRAG_TYPE = 'application/x-twasta-document'

/**
 * One document in the library, as a row.
 *
 * The whole card is the target rather than the title alone: the old table put a
 * link in the first cell and left the other three quarters of the row inert,
 * which is a smaller hit area than the row it looks like.
 */
function DocumentRow({ row, userKey, nameOf, typeName, folderName, draggable, onOpen }: {
  row: DmsDocumentRow
  userKey?: string
  nameOf: (key: string) => string
  typeName: string
  folderName: string
  draggable: boolean
  onOpen: () => void
}) {
  const { label, tone, meaning, facts } = useDocumentFacts(row, userKey, nameOf)
  const ownerName = personNameOrYou(undefined, row.owner_user, userKey)
    || nameOf(String(row.owner_user || ''))

  return (
    <button type="button" onClick={onOpen} {...dragProps(row, draggable)}
            style={{ ...rowStyle, width: '100%', textAlign: 'left', cursor: 'pointer' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {/* The KIND comes first and never wraps away: it is what tells a
              reader whether they are looking at a policy or an invoice, and
              the old list did not show it at all. */}
          {typeName ? <FactChip tone="accent">{typeName}</FactChip> : null}
          <span style={{ fontSize: TYPE.heading, fontWeight: 600, color: T.accent }}>
            {row.title || 'Untitled'}
          </span>
          <StatusChip tone={tone}>{label}</StatusChip>
          {facts.map((fact) => (
            <FactChip key={fact.text} tone={fact.tone}>{fact.text}</FactChip>
          ))}
          {row.is_master && row.child_count ? (
            <FactChip title="This document is a set made up of other documents">
              A set of {row.child_count}
            </FactChip>
          ) : null}
        </div>
        {meaning ? <div style={{ ...explainStyle, marginTop: 3 }}>{meaning}</div> : null}
        <div style={{ ...metaStyle, marginTop: 4 }}>
          {folderName ? `${folderName} · ` : ''}
          {ownerName ? `Looked after by ${ownerName} · ` : ''}
          Updated {formatWhen(row.updated_at)}
        </div>
      </div>
    </button>
  )
}

/**
 * One document as a card, with the first page of the document on it.
 *
 * The preview is the point: a library of names tells you nothing about which
 * "Rate card 2026" is the one you want, and people recognise a document by
 * what it looks like long before they finish reading its title. It is
 * watermarked and permission-checked exactly as the viewer's pages are, it is
 * loaded lazily so a scroll does not render a hundred pages, and it is audited
 * as `thumbnail` rather than as a read — see `repository.thumbnail`.
 *
 * A file that cannot be rendered (a spreadsheet, a corrupt PDF, a host with no
 * renderer) falls back to naming the kind rather than to a broken image.
 */
function DocumentCard({ row, client, userKey, nameOf, typeName, folderName, draggable, onOpen }: {
  row: DmsDocumentRow
  client: DmsClient
  userKey?: string
  nameOf: (key: string) => string
  typeName: string
  folderName: string
  draggable: boolean
  onOpen: () => void
}) {
  const { label, tone, meaning, facts } = useDocumentFacts(row, userKey, nameOf)
  const [failed, setFailed] = useState(false)
  // Held back until the card is near the screen. The URL is what starts the
  // fetch below, so withholding it is what makes the preview lazy on the
  // client-fetched path — see `useNearViewport`.
  const { ref: previewRef, near } = useNearViewport()
  const previewable = row.rights.view && Boolean(client.thumbnailUrl)
  const url = near && previewable ? client.thumbnailUrl!(row.id) : ''
  // Fetched through the client, not by the browser: a project user's session
  // is headers and a bearer token, which an <img src> cannot carry — every
  // card came back 404 for them.
  const src = useAuthedSrc(client, url)
  // Waiting, as opposed to having nothing: the card is not on screen yet, or
  // its preview is in flight. "No preview" is a statement about the document
  // and must not be made about a fetch that has not finished — it used to
  // flash on every card while the blob was resolving.
  const awaitingPreview = previewable && !failed && !src

  return (
    <button type="button" onClick={onOpen} {...dragProps(row, draggable)}
            title={row.title || 'Untitled'}
            style={{
              display: 'flex', flexDirection: 'column', gap: 0, padding: 0,
              border: `1px solid ${T.border}`, borderRadius: RADIUS.lg,
              background: T.surface, overflow: 'hidden', cursor: 'pointer',
              textAlign: 'left', fontFamily: 'inherit', minWidth: 0,
            }}>
      <div ref={previewRef} style={{
        position: 'relative', height: 150, background: T.surface2,
        borderBottom: `1px solid ${T.border}`, overflow: 'hidden',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {src && !failed ? (
          <img
            src={src}
            alt=""
            loading="lazy"
            onError={() => setFailed(true)}
            style={{
              width: '100%', height: '100%', objectFit: 'cover',
              objectPosition: 'top center', display: 'block',
            }}
          />
        ) : awaitingPreview ? null : (
          <span style={{ ...metaStyle, padding: '0 12px', textAlign: 'center' }}>
            {row.rights.view
              ? `${typeName || 'Document'} — no preview`
              : 'You cannot open this'}
          </span>
        )}
        <span style={{ position: 'absolute', top: 8, left: 8 }}>
          <StatusChip tone={tone}>{label}</StatusChip>
        </span>
      </div>

      <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
        {typeName ? (
          <span style={{ ...metaStyle, color: T.accent, fontWeight: 600 }}>{typeName}</span>
        ) : null}
        <span style={{
          fontSize: TYPE.body, fontWeight: 600, color: T.text, lineHeight: 1.35,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>
          {row.title || 'Untitled'}
        </span>
        {meaning ? (
          <span style={{
            ...mutedStyle,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {meaning}
          </span>
        ) : null}
        {facts.length ? (
          <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
            {facts.map((fact) => (
              <FactChip key={fact.text} tone={fact.tone}>{fact.text}</FactChip>
            ))}
          </span>
        ) : null}
        <span style={metaStyle}>
          {folderName ? `${folderName} · ` : ''}Updated {formatWhen(row.updated_at)}
        </span>
      </div>
    </button>
  )
}

/**
 * Folders down the side: where things are, how many are there, and where a
 * document goes when you drag it.
 *
 * Every folder carries a materialised path of ids (`/a/b/c`) — the same string
 * the ACL uses to make a grant on a parent cover its subtree — so the nesting
 * is derived here from `path`, with no second request and no recursion on the
 * server.
 */
function FolderRail({
  folders, value, counts, total, filtered, onChange, client, canCreate,
  onFolderCreated, onDropDocument, onMoveFolder,
}: {
  folders: DmsFolder[]
  value: string
  counts: Record<string, number>
  total: number
  filtered: boolean
  onChange: (id: string) => void
  client: DmsClient
  /** Whether this caller holds `add`. Making a folder is filing something new
   *  in the library, so it is the same right the server checks for it. */
  canCreate: boolean
  onFolderCreated: () => Promise<void> | void
  /** Absent when this host cannot move documents; the rail then takes no drops. */
  onDropDocument?: (documentId: string, folderId: string) => void | Promise<void>
  /** Absent when this host cannot rearrange folders; rows are then not draggable. */
  onMoveFolder?: (folderId: string, parentId: string, position?: number)
    => void | Promise<void>
}) {
  const ordered = useMemo(() => orderFolders(folders), [folders])
  /* Every branch arrives OPEN. A rail that starts folded hides the shape of
     the library from the person who filed it — the whole reason folders are a
     rail here and not a dropdown. Only what someone deliberately folds is
     remembered, by id, so a folder added later is never born hidden. */
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(
    () => new Set<string>())
  const [over, setOver] = useState<string | null>(null)
  /** Which row a dragged FOLDER is over, and what dropping there would do. */
  const [folderOver, setFolderOver] = useState<FolderDrop | null>(null)
  const [dragging, setDragging] = useState<string>('')

  /* The parent chain of every folder, walked from `parent_id` rather than
     read off the materialised `path`, so a branch still folds correctly while
     a just-moved folder's path is on its way back from the server. */
  const ancestorsOf = useMemo(() => {
    const parentOf = new Map<string, string>()
    for (const folder of folders) {
      parentOf.set(String(folder.id), String(folder.parent_id || ''))
    }
    const map = new Map<string, string[]>()
    for (const folder of folders) {
      const chain: string[] = []
      let current = parentOf.get(String(folder.id)) || ''
      // The same depth cap `orderFolders` walks with: a corrupt parent chain
      // must not spin here.
      while (current && chain.length <= 12) {
        chain.push(current)
        current = parentOf.get(current) || ''
      }
      map.set(String(folder.id), chain)
    }
    return map
  }, [folders])

  /** Only a folder with something inside it gets a twisty. */
  const withChildren = useMemo(() => new Set(
    folders.map((f) => String(f.parent_id || '')).filter(Boolean),
  ), [folders])

  const toggle = useCallback((folderId: string) => {
    setCollapsed((current) => {
      const next = new Set(current)
      if (!next.delete(folderId)) next.add(folderId)
      return next
    })
  }, [])

  /* A folder selected from somewhere else — a filter cleared, a document
     opened out of search — must not land inside a folded branch, where the
     rail would show nothing selected at all. */
  useEffect(() => {
    const ancestors = value ? ancestorsOf.get(String(value)) || [] : []
    if (!ancestors.length) return
    setCollapsed((current) => {
      if (!ancestors.some((id) => current.has(id))) return current
      const next = new Set(current)
      for (const id of ancestors) next.delete(id)
      return next
    })
  }, [value, ancestorsOf])

  /* Spring-loaded folders: holding a drag over a folded folder opens it, so
     somewhere currently out of sight is still somewhere you can file to. */
  const spring = useRef<{ id: string; timer: number } | null>(null)
  const cancelSpring = useCallback(() => {
    if (spring.current) window.clearTimeout(spring.current.timer)
    spring.current = null
  }, [])
  const springOpen = useCallback((folderId: string) => {
    if (!folderId || spring.current?.id === folderId) return
    cancelSpring()
    spring.current = {
      id: folderId,
      timer: window.setTimeout(() => {
        spring.current = null
        setCollapsed((current) => {
          if (!current.has(folderId)) return current
          const next = new Set(current)
          next.delete(folderId)
          return next
        })
      }, 600),
    }
  }, [cancelSpring])
  // A drag abandoned mid-hover must not leave a timer behind it.
  useEffect(() => cancelSpring, [cancelSpring])

  if (!folders.length && !(client.createFolder && canCreate)) return null

  /* A folded folder takes its whole subtree off the rail with it. Its count is
     the server's roll-up, so what is inside is still visible as a number. */
  const visible = ordered.filter(({ folder }) => !(
    ancestorsOf.get(String(folder.id)) || []
  ).some((id) => collapsed.has(id)))

  /* Every folder gets its count from the server's roll-up. "All documents"
     uses that too when it can, and falls back to the listing's own total —
     which is only the same number while nothing is filtering the list, so
     under a filter it is left off rather than shown as if it meant "all". */
  const allCount = counts[''] !== undefined || Object.keys(counts).length
    ? Object.entries(counts).reduce((sum, [key, n]) => (
      // Sum only the TOP-LEVEL entries plus unfiled: child folders are already
      // rolled into their parents, so adding every entry would double-count.
      key === '' || folders.find((f) => String(f.id) === key && !f.parent_id)
        ? sum + n : sum), 0)
    : (filtered ? undefined : total)

  /**
   * Where a folder dropped on this row would land.
   *
   * The top and bottom slivers of a row mean BETWEEN — reordering siblings —
   * and the middle means INSIDE. One row, both gestures, because a rail with
   * separate drop-lines between every folder is a row of two-pixel targets
   * nobody can hit; the same split every file manager uses.
   */
  const intentAt = (event: React.DragEvent, targetId: string): FolderDrop => {
    const box = event.currentTarget.getBoundingClientRect()
    const share = box.height ? (event.clientY - box.top) / box.height : 0.5
    if (!targetId) return { id: '', where: 'inside' }
    if (share < 0.28) return { id: targetId, where: 'before' }
    if (share > 0.72) return { id: targetId, where: 'after' }
    return { id: targetId, where: 'inside' }
  }

  /** Turn a drop into the move it means: a parent, and a slot under it. */
  const applyDrop = (moved: string, drop: FolderDrop) => {
    if (!onMoveFolder || !moved) return
    if (drop.where === 'inside') {
      if (drop.id === moved) return
      void onMoveFolder(moved, drop.id)
      return
    }
    const target = folders.find((f) => String(f.id) === drop.id)
    if (!target) return
    const parent = String(target.parent_id || '')
    // Counted WITHOUT the folder being moved, because that is the list the
    // server inserts into — counting it in would land a downward drag one
    // place short of where it was dropped.
    const siblings = ordered
      .map((entry) => entry.folder)
      .filter((f) => String(f.parent_id || '') === parent
        && String(f.id) !== moved)
    const at = siblings.findIndex((f) => String(f.id) === drop.id)
    if (at < 0) return
    void onMoveFolder(moved, parent, drop.where === 'before' ? at : at + 1)
  }

  const dropProps = (folderId: string) => ({
    onDragOver: (event: React.DragEvent) => {
      const types = event.dataTransfer.types
      if (onMoveFolder && types.includes(FOLDER_DRAG_TYPE)) {
        const intent = intentAt(event, folderId)
        // A folder cannot be dropped into itself or its own subtree; showing
        // no marker is how the rail says so before the drop is attempted.
        if (!wouldOrphan(folders, dragging, intent)) {
          event.preventDefault()
          event.dataTransfer.dropEffect = 'move'
          setFolderOver(intent)
          // Opening on hover only where the drop would go INSIDE; a re-order
          // past a folded folder has no reason to unfold it.
          if (intent.where === 'inside' && collapsed.has(folderId)) {
            springOpen(folderId)
          } else if (spring.current?.id === folderId) {
            cancelSpring()
          }
        }
        return
      }
      if (!onDropDocument || !types.includes(DRAG_TYPE)) return
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
      setOver(folderId)
      if (collapsed.has(folderId)) springOpen(folderId)
    },
    onDragLeave: () => {
      if (spring.current?.id === folderId) cancelSpring()
      setOver((current) => (current === folderId ? null : current))
      setFolderOver((current) => (current && current.id === folderId
        ? null : current))
    },
    onDrop: (event: React.DragEvent) => {
      event.preventDefault()
      cancelSpring()
      const intent = folderOver
      setOver(null)
      setFolderOver(null)
      const movedFolder = event.dataTransfer.getData(FOLDER_DRAG_TYPE)
      if (movedFolder) {
        applyDrop(movedFolder, intent || intentAt(event, folderId))
        return
      }
      const id = event.dataTransfer.getData(DRAG_TYPE)
      if (id) void onDropDocument?.(id, folderId)
    },
  })

  /** What makes a row a drag HANDLE. Only where the host can act on the drop. */
  const dragProps = (folderId: string) => (onMoveFolder ? {
    draggable: true,
    onDragStart: (event: React.DragEvent) => {
      event.dataTransfer.setData(FOLDER_DRAG_TYPE, folderId)
      event.dataTransfer.effectAllowed = 'move'
      setDragging(folderId)
    },
    onDragEnd: () => {
      cancelSpring()
      setDragging('')
      setFolderOver(null)
      setOver(null)
    },
  } : {})

  return (
    <aside style={{
      width: 232, flexShrink: 0, borderRight: `1px solid ${T.border}`,
      padding: '14px 10px', overflow: 'auto', background: T.surface,
    }}>
      <div style={{ ...sectionTitleStyle, padding: '0 8px' }}>Folders</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* The root takes drops too: a folder dragged here comes out of
            whatever it was filed in and joins the top level, last. */}
        <FolderRow label="All documents" count={allCount} depth={0}
                   selected={!value}
                   dropping={over === '' || folderOver?.id === ''}
                   onClick={() => onChange('')} {...dropProps('')} />
        {visible.map(({ folder, depth }) => (
          <FolderRow
            key={folder.id}
            label={folder.name}
            count={counts[String(folder.id)]}
            depth={depth}
            expandable={withChildren.has(String(folder.id))}
            expanded={!collapsed.has(String(folder.id))}
            onToggle={() => toggle(String(folder.id))}
            selected={String(folder.id) === value}
            dropping={over === String(folder.id)
              || (folderOver?.where === 'inside'
                  && folderOver.id === String(folder.id))}
            insert={folderOver && folderOver.id === String(folder.id)
              && folderOver.where !== 'inside' ? folderOver.where : undefined}
            dimmed={dragging === String(folder.id)}
            onClick={() => onChange(String(folder.id))}
            {...dragProps(String(folder.id))}
            {...dropProps(String(folder.id))}
          />
        ))}
      </div>
      {onMoveFolder && folders.length > 1 ? (
        <div style={{ ...explainStyle, padding: '6px 8px 0' }}>
          Drag a folder to re-order it, or onto another to file it inside.
        </div>
      ) : null}
      {client.createFolder && canCreate ? (
        <div style={{ marginTop: 8 }}>
          <NewFolderButton client={client} parentId={value} onDone={onFolderCreated} />
        </div>
      ) : null}
    </aside>
  )
}

/** A folder being dragged, and what dropping it where it is would mean. */
interface FolderDrop {
  /** The row under the pointer; `''` is "All documents", i.e. the root. */
  id: string
  where: 'before' | 'after' | 'inside'
}

const FOLDER_DRAG_TYPE = 'application/x-twasta-folder'

/**
 * Whether the drop would put a folder inside itself.
 *
 * Not a nicety: the two rows would point at each other and the whole branch —
 * with everything filed in it — would drop off the tree. The server refuses it
 * as well; this is so the rail never offers the gesture in the first place.
 */
function wouldOrphan(folders: DmsFolder[], moved: string,
                     drop: FolderDrop): boolean {
  if (!moved) return false
  if (drop.id === moved) return true
  const target = folders.find((f) => String(f.id) === drop.id)
  const parent = drop.where === 'inside' ? drop.id : String(target?.parent_id || '')
  if (!parent) return false
  const path = String(folders.find((f) => String(f.id) === parent)?.path || '')
  return path.split('/').filter(Boolean).includes(moved)
}

function FolderRow({ label, count, depth, selected, dropping, insert, dimmed,
                     expandable, expanded, onToggle, onClick, ...rest }: {
  label: string
  count?: number
  depth: number
  selected: boolean
  /** Whether this folder has anything under it to fold away. */
  expandable?: boolean
  expanded?: boolean
  onToggle?: () => void
  dropping?: boolean
  /** Shows the line a re-order would drop the folder on. */
  insert?: 'before' | 'after'
  /** The row currently being dragged, faded so the gap it leaves is visible. */
  dimmed?: boolean
  onClick: () => void
} & React.HTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-expanded={expandable ? Boolean(expanded) : undefined}
      {...rest}
      onKeyDown={(event) => {
        // The tree keys people already know, so folding never needs the
        // pointer: right opens the branch under the cursor, left closes it.
        if (!expandable) return
        if (event.key === 'ArrowRight' && !expanded) {
          event.preventDefault()
          onToggle?.()
        } else if (event.key === 'ArrowLeft' && expanded) {
          event.preventDefault()
          onToggle?.()
        }
      }}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
        padding: '7px 10px', paddingLeft: 10 + depth * 14,
        border: dropping ? `1px dashed ${T.accent}` : '1px solid transparent',
        borderTop: insert === 'before' ? `2px solid ${T.accent}` : undefined,
        borderBottom: insert === 'after' ? `2px solid ${T.accent}` : undefined,
        borderRadius: RADIUS.md,
        background: dropping ? T.accentWeak
          : selected ? T.accentWeak : 'transparent',
        color: selected || dropping ? T.accent : T.text,
        fontWeight: selected ? 600 : 400,
        opacity: dimmed ? 0.45 : 1,
        fontSize: TYPE.meta, fontFamily: 'inherit',
        textAlign: 'left', cursor: 'pointer',
      }}
    >
      {/* A span rather than a nested button: the row IS a button, and the
          twisty has to sit inside it to stay on the same drag handle. The
          keyboard reaches it through the row's own arrow keys. */}
      <span
        role={expandable ? 'button' : undefined}
        aria-label={expandable
          ? `${expanded ? 'Collapse' : 'Expand'} ${label}` : undefined}
        title={expandable ? (expanded ? 'Collapse' : 'Expand') : undefined}
        onMouseDown={expandable ? (event) => event.stopPropagation() : undefined}
        onClick={expandable ? (event) => {
          // Folding a branch is not selecting it: someone tidying the rail
          // must not have the listing re-filtered under them.
          event.stopPropagation()
          onToggle?.()
        } : undefined}
        style={{
          width: 14, flexShrink: 0, display: 'inline-flex',
          alignItems: 'center', justifyContent: 'center',
          color: selected || dropping ? T.accent : T.textFaint,
          cursor: expandable ? 'pointer' : 'default',
          pointerEvents: expandable ? 'auto' : 'none',
          transition: 'transform 120ms ease',
          transform: expandable && expanded ? 'rotate(90deg)' : 'none',
        }}
      >
        {expandable ? <Icon name="chevronRight" size={12} width={1.7} /> : null}
      </span>
      <span style={{
        flex: 1, minWidth: 0, overflow: 'hidden',
        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        pointerEvents: 'none',
      }}>
        {label}
      </span>
      {typeof count === 'number' ? (
        <span style={{
          fontSize: TYPE.small, pointerEvents: 'none',
          color: selected || dropping ? T.accent : T.textFaint,
        }}>
          {count}
        </span>
      ) : null}
    </button>
  )
}

function NewFolderButton({ client, parentId, onDone }: {
  client: DmsClient
  parentId: string
  onDone: () => Promise<void> | void
}) {
  const [name, setName] = useState('')
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  if (!open) {
    return (
      <button type="button" style={{ ...btnStyle, width: '100%', borderStyle: 'dashed' }}
              onClick={() => setOpen(true)}
              title={parentId
                ? 'Create a folder inside the one selected'
                : 'Create a folder at the top level'}>
        New folder
      </button>
    )
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <input
        autoFocus
        value={name}
        placeholder="Folder name"
        style={{ ...inputStyle, width: '100%' }}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') { setOpen(false); setName('') } }}
      />
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          type="button"
          style={{ ...btnStyle, flex: 1 }}
          disabled={busy || !name.trim()}
          onClick={async () => {
            setBusy(true)
            setError('')
            try {
              await client.createFolder?.({ name: name.trim(), parentId })
              setName('')
              setOpen(false)
              await onDone()
            } catch (createError) {
              setError(createError instanceof Error ? createError.message
                : 'The folder could not be created.')
            } finally {
              setBusy(false)
            }
          }}
        >
          {busy ? 'Creating…' : 'Create'}
        </button>
        <button type="button" style={btnStyle}
                onClick={() => { setOpen(false); setName(''); setError('') }}>
          Cancel
        </button>
      </div>
      {error ? <span style={errStyle}>{error}</span> : null}
    </div>
  )
}

/** The administrator's control for rebuilding the search, on their own screen. */
function SearchIndexStrip({ reindex, onStart }: {
  reindex: DmsReindexStatus | null
  onStart: () => void
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
      borderBottom: `1px solid ${T.border}`, background: T.surface,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: TYPE.meta, fontWeight: 600 }}>Search index</div>
        <div style={{ ...mutedStyle, marginTop: 2 }}>
          {reindex?.active
            ? `Rebuilding: ${reindex.done + reindex.parked} of ${reindex.total} documents done.`
            : 'Rebuild after changing the AI settings. You will see how much work it is before anything runs.'}
        </div>
      </div>
      {reindex?.active ? (
        <progress value={reindex.done + reindex.parked}
                  max={reindex.total || 1} style={{ width: 140 }} />
      ) : (
        <button type="button" style={btnStyle} onClick={onStart}>Rebuild the search</button>
      )}
    </div>
  )
}

/**
 * The answer text with its `[n]` markers turned into links. Clicking one opens
 * the cited document at the cited page with the passage shown — the marker IS
 * the citation, so it should behave like one rather than being inert text.
 */
/**
 * The answer, formatted, with its citation markers still clickable.
 *
 * An answer is MARKDOWN — the same markdown the assistant replies in, from the
 * same kind of model — so it arrives with headings, bold labels and nested
 * bullets. Rendered as raw text it read as `### Stage 1:` and `* **Objective:**`,
 * literal hashes and asterisks down the page. It goes through `MarkdownText`,
 * the one renderer every other assistant surface already uses, so this panel
 * and the assistant format alike.
 *
 * The citations are why this is not simply `<MarkdownText>`. A grounded answer
 * cites INSIDE its sentences, so a `[1]` cannot be lifted out of the prose to
 * be made clickable without moving it away from the clause it supports —
 * splitting the text on markers first is what cost the formatting. Instead the
 * renderer is handed `renderMarker` and calls back mid-sentence, which keeps
 * the markdown whole and the markers where the answer put them.
 */
function AnswerWithCitations({ answer, citations, onOpen }: {
  answer: string
  citations: DmsAnswer['citations']
  onOpen: (id: string, focus?: { page?: number | null; excerpt?: string }) => void
}) {
  const byNumber = new Map(citations.map((c) => [c.n, c]))
  return (
    <MarkdownText
      text={answer}
      renderMarker={(numbers) => numbers.map((n, j) => {
        const citation = byNumber.get(n)
        // A number nothing was cited under stays plain text, exactly as
        // before — it is the model's marker, not a source anyone can open.
        if (!citation) return <span key={j}>[{n}]</span>
        return (
          <button key={j} type="button" style={citeMarkerStyle}
                  title={`${citation.title || 'Untitled'}${citation.page ? ` — page ${citation.page}` : ''}`}
                  onClick={() => onOpen(citation.document_id,
                    { page: citation.page, excerpt: citation.excerpt })}>
            [{n}]
          </button>
        )
      })}
    />
  )
}

/**
 * Ask, and what came back.
 *
 * CONTROLLED, deliberately. Opening a citation replaces this whole view with
 * the document, and coming back used to remount this panel from scratch — the
 * answer the reader had just followed a link out of was gone, with no way back
 * to it but retyping the question. The state lives in `RepositoryView`, which
 * is not unmounted by that round trip, so the answer, its sources and the
 * question are all still there when the reader returns.
 */
function AskPanel({ client, state, onState, onOpen }: {
  client: DmsClient
  state: AskState
  onState: React.Dispatch<React.SetStateAction<AskState>>
  onOpen: (id: string, focus?: { page?: number | null; excerpt?: string }) => void
}) {
  const [busy, setBusy] = useState(false)
  const { question, answer, error, rated, openCitations } = state

  const ask = useCallback(async () => {
    if (!question.trim()) return
    setBusy(true)
    onState((prior) => ({ ...prior, error: '', rated: false }))
    try {
      const result = await client.ask(question)
      onState((prior) => ({ ...prior, answer: result, error: '',
                            openCitations: [] }))
    } catch (askError) {
      onState((prior) => ({
        ...prior,
        answer: null,
        error: askError instanceof Error ? askError.message
          : 'The assistant could not answer just now.',
      }))
    } finally {
      setBusy(false)
    }
  }, [client, onState, question])

  const rate = useCallback(async (rating: 'up' | 'down', category?: string) => {
    try {
      await client.feedback({
        rating, category, question,
        answer: answer?.answer || '',
        documentId: answer?.citations?.[0]?.document_id,
      })
    } catch {
      // Feedback is never worth blocking the reader on.
    } finally {
      onState((prior) => ({ ...prior, rated: true }))
    }
  }, [answer, client, onState, question])

  return (
    <div style={{ ...scrollAreaStyle, padding: 20 }}>
      <div style={{ maxWidth: 780, margin: '0 auto' }}>
        <p style={{ ...explainStyle, marginBottom: 12 }}>
          Answers come only from documents you are allowed to see, and always
          show which ones they came from.
        </p>

        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={question}
            placeholder="What would you like to know?"
            aria-label="Your question"
            onChange={(event) => {
              const value = event.target.value
              onState((prior) => ({ ...prior, question: value }))
            }}
            onKeyDown={(event) => { if (event.key === 'Enter') void ask() }}
            style={{ ...searchStyle, flex: 1 }}
          />
          <button type="button" style={btnPrimaryStyle} disabled={busy}
                  onClick={() => void ask()}>
            {busy ? 'Asking…' : 'Ask'}
          </button>
          {answer ? (
            <button type="button" style={btnStyle}
                    onClick={() => onState(EMPTY_ASK)}>
              Start again
            </button>
          ) : null}
        </div>

        {error ? <p style={errStyle}>{error}</p> : null}

        {answer ? (
          <div style={{ marginTop: 18 }}>
            <div style={{ ...cardStyle, fontSize: TYPE.body, lineHeight: 1.7 }}>
              <AnswerWithCitations answer={answer.answer}
                                   citations={answer.citations} onOpen={onOpen} />
            </div>

            {answer.citations.length > 0 ? (
              <section style={{ marginTop: 18 }}>
                <h3 style={{ ...headingStyle, marginBottom: 10 }}>Where this came from</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {answer.citations.map((citation) => {
                    // Which document an answer rests on is the point of this
                    // section, so every source stays named and openable. What
                    // folds is the PASSAGE — a paragraph per citation turned a
                    // three-source answer into a page of quotations to scroll
                    // past before the next document could be seen.
                    const citeKey = `${citation.document_id}-${citation.n}`
                    const passageOpen = openCitations.includes(citeKey)
                    const togglePassage = () => onState((prior) => ({
                      ...prior,
                      openCitations: prior.openCitations.includes(citeKey)
                        ? prior.openCitations.filter((c) => c !== citeKey)
                        : [...prior.openCitations, citeKey],
                    }))
                    return (
                    <div key={citeKey}
                         style={{ ...cardStyle, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <span style={{
                        width: 24, height: 24, flexShrink: 0, borderRadius: RADIUS.full,
                        background: T.accentWeak, color: T.accent, fontSize: TYPE.small,
                        fontWeight: 700, display: 'inline-flex',
                        alignItems: 'center', justifyContent: 'center',
                      }}>
                        {citation.n}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* The document's NAME, not a control. Leaving the
                            answer to open a document is a deliberate act, so
                            it is asked for by name — "Read document", beside
                            this — rather than by clicking a title that gives
                            no sign it will navigate away. */}
                        <div style={{ fontSize: TYPE.body, fontWeight: 600,
                                      color: T.text, overflowWrap: 'anywhere' }}>
                          {citation.title || 'Untitled'}
                        </div>
                        <div style={{ ...metaStyle, marginTop: 3 }}>
                          Version {citation.version || '—'}
                          {citation.effective_date ? `, in force since ${citation.effective_date}` : ''}
                          {citation.page ? `, page ${citation.page}` : ''}
                        </div>
                        {citation.excerpt ? (
                          <>
                            {/* Reading the passage and reading the document
                                are separate asks, so they are separate
                                controls: this one unfolds a quotation in
                                place, "Read document" leaves the answer. */}
                            <button
                              type="button"
                              aria-expanded={passageOpen}
                              aria-controls={`cite-${citeKey}`}
                              onClick={togglePassage}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                marginTop: 6, padding: '2px 0', border: 0,
                                background: 'none', color: T.textSecondary,
                                fontSize: TYPE.small, fontFamily: 'inherit',
                                cursor: 'pointer',
                              }}
                            >
                              <span style={{
                                display: 'inline-flex',
                                transition: 'transform 150ms ease',
                                transform: passageOpen ? 'rotate(90deg)' : 'none',
                              }}>
                                <Icon name="chevronRight" size={11} width={1.7} />
                              </span>
                              {passageOpen ? 'Hide the cited passage'
                                : 'Show the cited passage'}
                            </button>
                            <div id={`cite-${citeKey}`} hidden={!passageOpen}
                                 style={{
                                   marginTop: 8, paddingLeft: 12,
                                   borderLeft: `3px solid ${T.border}`,
                                   fontSize: TYPE.meta, lineHeight: 1.6,
                                   color: T.textSecondary,
                                 }}>
                              {citation.excerpt}
                            </div>
                          </>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        style={{ ...linkStyle, fontSize: TYPE.meta,
                                 fontWeight: 600, flexShrink: 0,
                                 whiteSpace: 'nowrap' }}
                        title={citation.page
                          ? `Opens at page ${citation.page}, with the cited passage`
                          : 'Opens this document at the cited passage'}
                        onClick={() => onOpen(citation.document_id,
                          { page: citation.page, excerpt: citation.excerpt })}>
                        Read document
                      </button>
                    </div>
                    )
                  })}
                </div>
              </section>
            ) : (
              <p style={{ ...explainStyle, marginTop: 14 }}>
                Nothing in the documents available to you covered this.
              </p>
            )}

            {!rated ? (
              <div style={{ ...cardStyle, marginTop: 18, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: TYPE.meta, color: T.textSecondary }}>Was this right?</span>
                <div style={{ flex: 1 }} />
                <button type="button" style={btnStyle} onClick={() => void rate('up')}>
                  Yes, that helped
                </button>
                <button type="button" style={btnStyle}
                        title="Goes to whoever looks after the document"
                        onClick={() => void rate('down', 'source_error')}>
                  The document itself is out of date
                </button>
                <button type="button" style={btnStyle}
                        title="Goes to whoever looks after the assistant"
                        onClick={() => void rate('down', 'ai_defect')}>
                  It misread the document
                </button>
              </div>
            ) : (
              <p style={{ ...explainStyle, marginTop: 18 }}>
                Thank you — this has been sent to the right owner.
              </p>
            )}
          </div>
        ) : null}
      </div>
    </div>
  )
}

/**
 * One list of everything the repository needs from this person.
 *
 * Acknowledgements, approvals and signatures are genuinely different
 * obligations, so they stay as separate sections with their own explanation —
 * but they are on one screen, and the screen exists whether or not there is
 * anything on it.
 */
function WaitingForYou({
  client, acks, pendingCount, signatures, onOpen, onAcknowledged, onDecided,
}: {
  client: DmsClient
  acks: DmsOutstandingAck[]
  pendingCount: number
  signatures: DmsPendingSignature[]
  onOpen: (id: string) => void
  onAcknowledged: () => Promise<void> | void
  onDecided: () => Promise<void> | void
}) {
  if (!acks.length && !pendingCount && !signatures.length) {
    return (
      <div style={scrollAreaStyle}>
        <EmptyState
          headline="Nothing is waiting for you"
          hint="When a document needs your signature or your approval, or you are asked to confirm you have read one, it will appear here."
        />
      </div>
    )
  }

  return (
    <div style={{ ...scrollAreaStyle, padding: 20 }}>
      <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 24 }}>
        {acks.length ? (
          <section>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <h3 style={headingStyle}>Confirm you have read these</h3>
              <span style={mutedStyle}>
                {acks.length} document{acks.length === 1 ? '' : 's'}
              </span>
            </div>
            <p style={{ ...explainStyle, margin: '4px 0 12px' }}>
              Confirming records that you have read <strong>this exact version</strong>.
              If a new version is released later, you will be asked again.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {acks.map((ack) => (
                <AckRow key={ack.assignment_id} client={client} ack={ack}
                        onOpen={onOpen} onDone={onAcknowledged} />
              ))}
            </div>
          </section>
        ) : null}

        {pendingCount ? (
          <section>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <h3 style={headingStyle}>Your approval is needed</h3>
              <span style={mutedStyle}>
                {pendingCount} document{pendingCount === 1 ? '' : 's'}
              </span>
            </div>
            <p style={{ ...explainStyle, margin: '4px 0 12px' }}>
              Nothing changes for anyone else until every approver has said yes.
            </p>
            <ApprovalQueue client={client} onOpen={onOpen} onDecided={onDecided} />
          </section>
        ) : null}

        {signatures.length ? (
          <section>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <h3 style={headingStyle}>Your signature is needed</h3>
              {/* SIGNATURES, not documents: a two-party agreement where one
                  person holds both roles owes two, on one document. */}
              <span style={mutedStyle}>
                {signatures.length} signature{signatures.length === 1 ? '' : 's'}
              </span>
            </div>
            <p style={{ ...explainStyle, margin: '4px 0 12px' }}>
              Open the document to read what you are signing. A signature is
              applied on the document itself — never from a list.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {signatures.map((sig) => (
                <SignatureRow key={sig.slot_id} signature={sig} onOpen={onOpen} />
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </div>
  )
}

/**
 * One document waiting on the reader's signature.
 *
 * Deliberately NOT a sign button. Signing is an attestation to what a
 * particular document says, and the attestation text, the version being signed
 * and the credential prompt all live on the document — a list that could apply
 * a signature would be a list somebody could sign without reading.
 */
function SignatureRow({ signature, onOpen }: {
  signature: DmsPendingSignature
  onOpen: (id: string) => void
}) {
  return (
    <div style={{ ...rowStyle, alignItems: 'flex-start' }}>
      <div style={{
        width: 4, alignSelf: 'stretch', borderRadius: RADIUS.full,
        flexShrink: 0, background: T.surface2,
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button type="button"
                  style={{ ...linkStyle, fontSize: TYPE.heading, fontWeight: 600 }}
                  onClick={() => onOpen(signature.document_id)}>
            {signature.title || 'Untitled document'}
          </button>
          {signature.role ? <FactChip>As {signature.role}</FactChip> : null}
          {signature.total > 1 ? (
            <FactChip>{signature.signed} of {signature.total} signed</FactChip>
          ) : null}
        </div>
        <div style={{ ...metaStyle, marginTop: 3 }}>
          {signature.meaning || signature.slot_key}
          {signature.requested_at
            ? ` · asked ${formatWhen(signature.requested_at)}` : ''}
        </div>
      </div>
      <button type="button" style={btnStyle}
              onClick={() => onOpen(signature.document_id)}>
        Read &amp; sign
      </button>
    </div>
  )
}

function AckRow({ client, ack, onOpen, onDone }: {
  client: DmsClient
  ack: DmsOutstandingAck
  onOpen: (id: string) => void
  onDone: () => Promise<void> | void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  return (
    <div style={{
      ...rowStyle,
      alignItems: 'flex-start',
      borderColor: ack.overdue ? T.error : T.border,
    }}>
      <div style={{
        width: 4, alignSelf: 'stretch', borderRadius: RADIUS.full, flexShrink: 0,
        background: ack.overdue ? T.error : T.surface2,
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" style={{ ...linkStyle, fontSize: TYPE.heading, fontWeight: 600 }}
                  onClick={() => onOpen(ack.document_id)}>
            {ack.title}
          </button>
          {ack.overdue
            ? <FactChip tone="bad">Overdue</FactChip>
            : ack.due_at ? <FactChip>Due {formatWhen(ack.due_at)}</FactChip> : null}
        </div>
        <div style={{ ...explainStyle, marginTop: 4 }}>
          Version {ack.version || '—'}
          {ack.due_at ? '' : ' · no date has been set for this one'}
        </div>
        {error ? <div style={errStyle}>{error}</div> : null}
      </div>
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        <button type="button" style={btnStyle} onClick={() => onOpen(ack.document_id)}>
          Open it
        </button>
        <button
          type="button"
          disabled={busy}
          style={btnPrimaryStyle}
          onClick={async () => {
            setBusy(true)
            setError('')
            try {
              await client.acknowledge(ack.assignment_id)
              await onDone()
            } catch (ackError) {
              setError(ackError instanceof Error ? ackError.message
                : 'That could not be recorded just now.')
            } finally {
              setBusy(false)
            }
          }}
        >
          {busy ? 'Recording…' : 'I have read it'}
        </button>
      </div>
    </div>
  )
}

const citeMarkerStyle: React.CSSProperties = {
  border: 'none', background: 'none', padding: '0 1px', cursor: 'pointer',
  color: T.accent, fontSize: 'inherit',
  fontWeight: 600, verticalAlign: 'baseline',
}

export default RepositoryView
